// SPDX-License-Identifier: LGPL-2.1-or-later
// The colour theme: the system's (light or dark, high contrast when the system asks for more contrast), or light,
// dark or high contrast as chosen, kept in this browser. The page carries it as `data-theme` (light, dark or
// contrast), which picks the colour tokens in styles.css; parts that draw with those colours (the viewport's
// background) redraw on THEME_EVENT.

export type ThemeChoice = "system" | "light" | "dark" | "contrast";
export type Theme = "light" | "dark" | "contrast";

export const THEMES: readonly { readonly id: ThemeChoice; readonly name: string }[] = [
  { id: "system", name: "As the system" },
  { id: "light", name: "Light colours" },
  { id: "dark", name: "Dark colours" },
  { id: "contrast", name: "High contrast" },
];

/** Fired on the window when the theme changes (by choice or by the system's setting). */
export const THEME_EVENT = "formverk-theme";

const STORAGE_KEY = "formverk.theme";

function stored(): ThemeChoice {
  try {
    const v = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "contrast" || v === "system") return v;
  } catch {
    // No storage (a private window): the system's.
  }
  return "system";
}

let choice: ThemeChoice = stored();

const query = (q: string) => globalThis.matchMedia?.(q);

export function themeChoice(): ThemeChoice {
  return choice;
}

/** The theme in effect: the choice, or for "system" what the system asks for. */
export function theme(): Theme {
  if (choice !== "system") return choice;
  if (query("(prefers-contrast: more)")?.matches) return "contrast";
  return query("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

/** Sets the page's theme and tells what draws with its colours. */
export function applyTheme(): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  root.dataset.theme = theme();
  globalThis.dispatchEvent?.(new Event(THEME_EVENT));
}

/** Chooses a theme (and remembers it in this browser). */
export function setThemeChoice(next: ThemeChoice): void {
  choice = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, next);
  } catch {
    // Kept for this session only.
  }
  applyTheme();
}

/** Applies the theme now and again whenever the system's colour or contrast setting changes. */
export function startTheme(): void {
  applyTheme();
  for (const q of ["(prefers-color-scheme: dark)", "(prefers-contrast: more)"])
    query(q)?.addEventListener("change", () => {
      if (choice === "system") applyTheme();
    });
}
